'use client';

import type {
  BrokenIntegration,
  BrokenIntegrationProvider,
} from '@/data/integrations/use-broken-integrations';
import { Slack } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { ConfluenceLogo } from '@/app/settings/components/integrations-section/components/confluence-logo';
import { NotionLogo } from '@/app/settings/components/integrations-section/components/notion-logo';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useBrokenIntegrations } from '@/data/integrations/use-broken-integrations';
import { useInstallationStore } from '@/lib/stores/installation-store';
import { usePrStore } from '@/lib/stores/pr-store';

function formatBrokenIntegrations(integrations: BrokenIntegration[]) {
  if (integrations.length === 1) {
    return integrations[0]?.label ?? '';
  }

  if (integrations.length === 2) {
    return `${integrations[0]?.label} and ${integrations[1]?.label}`;
  }

  const leadingLabels = integrations
    .slice(0, -1)
    .map((integration) => integration.label)
    .join(', ');
  const trailingLabel = integrations.at(-1)?.label ?? '';
  return `${leadingLabels}, and ${trailingLabel}`;
}

export function IntegrationReauthBanner() {
  const { selectedInstallationId } = useInstallationStore();
  const { installationId: prInstallationId, isInitialized: isPrStoreInitialized } = usePrStore();

  // On PR pages, use the PR's installation so the banner shows and disconnects
  // the correct org's integrations — not the globally persisted selection.
  // When the PR store is initialized but installationId is null (org not in
  // the user's installations), pass undefined to disable the query entirely.
  const effectiveInstallationId = isPrStoreInitialized
    ? (prInstallationId ?? undefined)
    : selectedInstallationId;

  const { brokenIntegrations } = useBrokenIntegrations(effectiveInstallationId);

  return <IntegrationReauthBannerContent brokenIntegrations={brokenIntegrations} />;
}

const providerIcons: Record<BrokenIntegrationProvider, ReactNode> = {
  slack: <Slack className="h-4 w-4" />,
  linear: (
    <Image
      src="/linear.png"
      alt="Linear"
      width={16}
      height={16}
      className="size-4 object-contain"
    />
  ),
  jira: (
    <Image
      src="/jira-white.svg"
      alt="Jira"
      width={16}
      height={16}
      className="size-4 object-contain"
    />
  ),
  asana: (
    <Image
      src="/asana-white.png"
      alt="Asana"
      width={16}
      height={16}
      className="size-4 object-contain"
    />
  ),
  notion: (
    <span className="[&>svg]:h-4 [&>svg]:w-4">
      <NotionLogo />
    </span>
  ),
  confluence: (
    <span className="[&>svg]:size-4">
      <ConfluenceLogo />
    </span>
  ),
};

function IntegrationReauthBannerContent({
  brokenIntegrations,
}: {
  brokenIntegrations: BrokenIntegration[];
}) {
  const [showDisconnectAlert, setShowDisconnectAlert] = useState(false);

  if (brokenIntegrations.length === 0) {
    return null;
  }

  const integrationLabel = formatBrokenIntegrations(brokenIntegrations);
  const plural = brokenIntegrations.length > 1;
  const isAnyDisconnecting = brokenIntegrations.some((i) => i.isDisconnecting);

  const handleDisconnectAll = async () => {
    setShowDisconnectAlert(false);
    await Promise.allSettled(brokenIntegrations.map((integration) => integration.disconnect()));
  };

  return (
    <div className="border-b border-amber-500/30 bg-amber-950/20 px-7 py-3 md:px-14">
      <div className="flex items-center gap-3">
        <div className="flex shrink-0 items-center gap-1.5 text-amber-200/80">
          {brokenIntegrations.map((integration) => (
            <span key={integration.provider}>{providerIcons[integration.provider]}</span>
          ))}
        </div>
        <p className="text-sm text-amber-100/90">
          {integrationLabel} {plural ? 'need' : 'needs'} to be reconnected.
        </p>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="text-amber-200/70 hover:text-amber-100 hover:bg-amber-950/40"
            onClick={() => setShowDisconnectAlert(true)}
            disabled={isAnyDisconnecting}
          >
            {isAnyDisconnecting ? 'Disconnecting...' : 'Disconnect'}
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link href="/settings?tab=integrations">Reconnect</Link>
          </Button>
        </div>
      </div>

      <AlertDialog open={showDisconnectAlert} onOpenChange={setShowDisconnectAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Disconnect
              {integrationLabel}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {plural
                ? 'These integrations will be fully disconnected. You can always reconnect them later from settings.'
                : 'This integration will be fully disconnected. You can always reconnect it later from settings.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void handleDisconnectAll();
              }}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
